import React, { useState, useEffect, KeyboardEvent } from 'react';
import styles from './FormCheckbox.module.scss';

type Props = {
  selected?: boolean;
  disabled?: boolean;
  required?: boolean;
  onChange?: (selected: boolean) => void;
  onError?: (error: boolean) => void;
  onUpdateDataLayer?: () => void;
  dataId?: string;
  slots: {
    label?: React.ReactNode;
    error?: React.ReactNode;
    instructions?: React.ReactNode;
  };
};

export const Checkbox: React.FC<Props> = ({
  selected = false,
  disabled = false,
  required = false,
  dataId = '',
  onChange = () => {},
  onError = () => {},
  onUpdateDataLayer = () => {},
  slots,
}: Props) => {
  const [isSelected, setIsSelected] = useState(selected);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setIsSelected(selected);
  }, [selected]);

  const handleState = () => {
    const updatedState = !isSelected;
    setIsSelected(updatedState);
    onChange(updatedState);
    onUpdateDataLayer();
    setIsError(false);
    onError(false);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !disabled) {
      handleState();
    }
  };

  const checkError = (event: React.InvalidEvent<HTMLInputElement>) => {
    if (slots.error) {
      event.preventDefault();
    }
    if (event.target.validity.valueMissing) {
      setIsError(true);
      onError(true);
    } else {
      onError(false);
      setIsError(false);
    }
  };
  return (
    <div className="d-flex flex-column gap-3">
      <div
        className={`d-inline-flex align-items-start ${styles['checkbox-wrapper']} ${
          isError ? styles['form-element--error'] : ''
        }`}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && handleState()}
        onKeyUp={(event) => !disabled && handleKeyPress(event)}
      >
        <div
          className={`${styles.checkbox} d-flex ${isSelected ? styles.selected : ''} ${
            disabled ? styles.disabled : ''
          }`}
        >
          <input
            aria-label="checkbox"
            data-id={dataId}
            type="checkbox"
            required={required}
            checked={isSelected}
            onChange={() => {}}
            onInvalid={checkError}
            disabled={disabled}
          />
          {isSelected && (
            <em className={`d-flex bi-check ${styles['bi-check']} text-white`} aria-hidden="true" />
          )}
        </div>
        {slots.label && (
          <label
            htmlFor="checkbox"
            className={`${styles['label-slot']} ms-2 ${disabled ? styles.disabled : ''}`}
          >
            {slots.label}
          </label>
        )}
      </div>
      {slots.error && isError && (
        <span
          className={`${styles['error-message']} field-error-text text-error-400 ${
            disabled ? styles.disabled : ''
          }`}
        >
          {slots.error}
        </span>
      )}
      {slots.instructions && (
        <span className={`hint-text mt-1 ${disabled ? styles.disabled : ''}`}>
          {slots.instructions}
        </span>
      )}
    </div>
  );
};

export default Checkbox;
